<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Lesson Plans</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Lesson Plans</div>
      <div>
        You can add, edit, and delete all of Lesson Plans from this page.
      </div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-filter-wrapper">
          <div class="admin-search-container">
            <input
              type="text"
              class="admin-search-input"
              placeholder="Search..."
              v-model="searchQuery"
              @input="handleSearchChange"
            />
            <img
              src="/images/maker-cart/union.svg"
              alt="Search"
              class="admin-search-icon"
            />
          </div>
          <div class="admin-filter-container">
            <div>
              <SingleSelect
                placeholder="Sort By"
                v-model="currentSort"
                :options="sortOptions"
                prefix="Sort: "
                @change="handleSortChange"
              />
            </div>
          </div>
        </div>
        <div class="d-flex ga-3">
          <BaseButton
            variant="secondary"
            class="mx-2 my-md-auto"
            @click="handleAction({ type: 'add' })"
          >
            <div class="d-flex align-center ga-1">
              <img
                src="/images/icon/plus-circle.svg"
                width="20"
                height="20"
                alt="download-icon"
              />
              <span class="admin-button-text">Add Lesson Plans</span>
            </div>
          </BaseButton>
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-edit-action="true"
        :is-delete-action="true"
        @action="handleAction"
        @custom-action="handleCustomAction"
        @sort="handleTableSort"
        :use-api-sort="true"
        :customActions="customActions"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>

  <ConfirmationModal
    v-model="showModalDelete"
    variant="danger"
    title="Remove Lesson Plan"
    message="Are you sure want to remove this lesson plan?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="deleteSubmission(itemId)"
    @cancel="updateVisibilityModalDelete"
  />

  <ConfirmationModal
    v-model="showModalDuplicate"
    title="Duplicate Lesson Plan"
    message="Are you sure want to duplicate this lesson plan?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="duplicateSubmission(itemId)"
    @cancel="updateVisibilityModalDuplicate"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, inject, watch } from "vue";
import LessonPlanService from "@/services/lessonPlanService.js";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import TableData from "@/components/common/TableData.vue";
import { debounce } from "lodash";
import { useRouter } from "vue-router";
import BaseButton from "@/components/common/BaseButton.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";
import { capitalize } from "@/utils";

const showModalDelete = ref(false);
const showModalDuplicate = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const currentSort = ref("newest");
const notify = inject("notify");

const router = useRouter();

const data = ref({
  message: "",
  error: null,
  data: {
    page: 0,
    limit: 0,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const sortOptions = ref([
  { text: "Newest", value: "newest" },
  { text: "Oldest", value: "oldest" },
  { text: "Title A-Z", value: "title-a-z" },
  { text: "Title Z-A", value: "title-z-a" },
]);

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "id",
  order: "desc",
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

// Define table headers
const headers = ref([
  { text: "Status", value: "status", sortable: true },
  { text: "Title", value: "title", sortable: true },
  { text: "Grade", value: "grade", sortable: false },
  { text: "Subject", value: "subject", sortable: false },
  { text: "Domain", value: "domain", sortable: false },
]);

// Computed property to transform data for the table
const tableItems = computed(() => {
  return data.value.data.rows.map((item) => ({
    ...item,
    id: item.id || 0,
    grade: item?.grade?.map((grade) => grade.title).join(", ") || "",
    subject: item?.subject?.map((subject) => subject.title).join(", ") || "",
    domain: item?.domain?.map((domain) => domain.title).join(", ") || "",
    status: capitalize(item?.status) || "N/A",
  }));
});

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Sync sort options with filters
watch(
  [() => filters.value.sort_by, () => filters.value.order],
  ([sortBy, order]) => {
    if (sortBy === "id" && order === "desc") {
      currentSort.value = "newest";
    } else if (sortBy === "id" && order === "asc") {
      currentSort.value = "oldest";
    } else if (sortBy === "Title" && order === "asc") {
      currentSort.value = "title-a-z";
    } else if (sortBy === "Title" && order === "desc") {
      currentSort.value = "title-z-a";
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await getAllSubmission();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

const handleTableSort = ({ sort_by, sort_dir }) => {
  filters.value.sort_by = sort_by;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAllSubmission();
};

const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAllSubmission();
}, 300);

const handleSortChange = () => {
  switch (currentSort.value) {
    case "newest":
      filters.value.sort_by = "id";
      filters.value.order = "desc";
      break;
    case "oldest":
      filters.value.sort_by = "id";
      filters.value.order = "asc";
      break;
    case "title-a-z":
      filters.value.sort_by = "title";
      filters.value.order = "asc";
      break;
    case "title-z-a":
      filters.value.sort_by = "title";
      filters.value.order = "desc";
      break;
    default:
      filters.value.sort_by = "id";
      filters.value.order = "desc";
  }
  filters.value.page = 1;
  currentPage.value = 1;
  getAllSubmission();
};

const handleAction = (action) => {
  if (action.type === "edit") {
    router.push({
      name: "LessonPlansEditPage",
      params: { id: action.item.id },
    });
  } else if (action.type === "delete") {
    itemId.value = action.item.id;
    showModalDelete.value = true;
  } else if (action.type === "view") {
    // router.push({
    //   name: "LessonPlansViewPage",
    //   params: { id: action.item.id },
    // });
  } else if (action.type === "add") {
    router.push({
      name: "LessonPlansFormPage",
    });
  }
};

const getAllSubmission = async () => {
  try {
    tableLoading.value = true;

    const response = await LessonPlanService.getLessons(filters.value);

    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching lesson plans:", error.message);
    data.value.data.rows = [];
    data.value.data.total_pages = 0;
  } finally {
    tableLoading.value = false;
  }
};

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAllSubmission();
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const deleteSubmission = async (id) => {
  isLoading.value = true;
  try {
    const response = await LessonPlanService.deleteLesson(id);
    if (response.status === 200) {
      isLoading.value = false;
      showModalDelete.value = false;
      await getAllSubmission();
      notify.success("Lesson plan deleted successfully", "Success");
    }
  } catch (error) {
    console.error(error.message);
    notify.error("Failed to delete lesson plan", "Error");
  } finally {
    isLoading.value = false;
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Define custom actions
const customActions = ref([
  {
    type: "duplicate",
    icon: "/images/icon/copy.svg",
    isDisabled: (item) => item.status === "archived",
  },
]);

const handleCustomAction = async (action) => {
  if (action.type === "duplicate") {
    itemId.value = action.item.id;
    showModalDuplicate.value = true;
  }
};

const updateVisibilityModalDuplicate = () => {
  showModalDuplicate.value = !showModalDuplicate.value;
};

const duplicateSubmission = async (id) => {
  isLoading.value = true;
  try {
    await LessonPlanService.duplicateLesson(id);
    notify.success("Lesson plan duplicated successfully", "Success");
    await getAllSubmission();
  } catch (error) {
    console.error("Error duplicating lesson plan:", error.message);
    notify.error("Failed to duplicate lesson plan", "Error");
  } finally {
    isLoading.value = false;
    showModalDuplicate.value = false;
  }
};
</script>

<style scoped>
.admin-search-filter-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-filter-container {
  min-width: 200px;
  width: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .admin-search-filter-wrapper {
    width: 100%;
  }

  .admin-filter-container {
    width: 100%;
  }
}
</style>
